import { axiosFieldValidationErrorToFormErrors, hasFieldValidationError, LOGIN_METHOD } from '@gofranz/common';
import { parseApiError } from '@gofranz/common-components';
import { HttpNewForm } from '@gofranz/formshive-common';
import { Text, Title } from '@mantine/core';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { clearField } from '../../lib/clear-field';
import { useRustyState } from '../../state';
import { FormFields, FormFieldsValues } from './Common';

export interface CreateFormProps {
  submitFormCb: (newForm: HttpNewForm) => Promise<void>;
}

export function CreateForm(props: CreateFormProps) {
  const { t } = useTranslation();
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState<{ title: string, message: string } | null>(null);
  const session = useRustyState((state) => state.getSession());

  // Check if user has Nostr pubkey (authenticated via Nostr)
  const hasNostrPubkey = session?.isLoggedIn &&
    (session.method === LOGIN_METHOD.NOSTR || session.method === LOGIN_METHOD.MANUAL) &&
    'publicKey' in session &&
    !!session.publicKey;

  const initialValues: FormFieldsValues = {
    title: '',
    filter_spam: false,
    check_challenge: false,
    redirect_url: undefined,
    auto_response_enabled: false,
    encryption_mode: 'none',
  };

  const formDataRef = useRef<FormFieldsValues>({ ...initialValues });
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const handleFormFieldsChange = useCallback((values: Partial<FormFieldsValues>) => {
    Object.assign(formDataRef.current, values);
  }, []);

  const submitForm = async () => {
    const newForm: HttpNewForm = {
      title: formDataRef.current.title,
      filter_spam: formDataRef.current.filter_spam,
      check_challenge: formDataRef.current.check_challenge,
      redirect_url: clearField(formDataRef.current.redirect_url),
      encryption_mode: formDataRef.current.encryption_mode,
    };
    setIsBusy(true);
    try {
      await props.submitFormCb(newForm);
      setError(null);
    } catch (e) {
      setError({
        ...parseApiError(e),
      });
      if (hasFieldValidationError(e)) {
        setFieldErrors(axiosFieldValidationErrorToFormErrors(e));
      }
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <>
      <Title order={3}>{t('forms.createForm')}</Title>
      <Text>{t('forms.createDescription')}</Text>
      <FormFields
        isEditing={false}
        isBusy={isBusy}
        hasError={error}
        fieldErrors={fieldErrors}
        submitCb={submitForm}
        formSubmitUrl="https://formshive.com/TBD"
        initialValues={initialValues}
        onValuesChange={handleFormFieldsChange}
        hasUnsavedChanges={false}
        hasNostrPubkey={hasNostrPubkey}
        onCancel={() => {
          console.log('Cancel clicked');
        }}
      />
    </>
  );
}
